import React, { useState, useEffect } from 'react';
import api from '../api';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import LeftPanel from '../components/LeftPanel';
import Step from '../components/Step';
import PrimaryButton from '../components/PrimaryButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

function InviteCollab() {
    const [invites, setInvites] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [form, setForm] = useState({ firstName: '', lastName: '', email: '' });
    const [schoolId, setSchoolId] = useState(null);
    const [userId, setUserId] = useState(null);
    const [userRole, setUserRole] = useState(null); // NEW: store user role
    const [schoolProfileExists, setSchoolProfileExists] = useState(false);
    const [schoolProfileCompleted, setSchoolProfileCompleted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editForm, setEditForm] = useState({ id: null, firstName: '', lastName: '', email: '', status: '' });
    const [editLoading, setEditLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [showInviteOnFirstLogin, setShowInviteOnFirstLogin] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('/api/public/me');
                if (res?.data?.authenticated) {
                    setSchoolId(res.data.schoolId);
                    setUserId(res.data.userId);
                    setUserRole(res.data.role); // NEW: set user role
                } else {
                    navigate('/login');
                }
            } catch (err) {
                navigate('/login');
            }
        })();
    }, [navigate]);

    // Initialize flag from navigation state (preferred) or localStorage so label renders correctly
    useEffect(() => {
        try {
            const fromNav = location && location.state && location.state.showInviteOnFirstLogin === true;
            if (fromNav) {
                // ensure localStorage is in sync and set state
                try { localStorage.setItem('showInviteOnFirstLogin', 'true'); } catch (e) { }
                setShowInviteOnFirstLogin(true);
                return;
            }
            const v = localStorage.getItem('showInviteOnFirstLogin');
            setShowInviteOnFirstLogin(v === 'true');
        } catch (e) {
            setShowInviteOnFirstLogin(false);
        }
    }, [location]);

    useEffect(() => {
        if (schoolId) {
            fetchInvites(schoolId);
            fetchContacts(schoolId);
        }
        // fetch school profile state for navigation decisions
        (async () => {
            try {
                const pr = await api.get('/api/public/school-profile');
                setSchoolProfileExists(Boolean(pr?.data?.exists));
                setSchoolProfileCompleted(Boolean(pr?.data?.completed));
            } catch (err) {
                setSchoolProfileExists(false);
                setSchoolProfileCompleted(false);
            }
        })();
    }, [schoolId]);

    const fetchInvites = async (sid) => {
        try {
            const res = await api.get(`/api/admin/invite/list?schoolId=${sid}`);
            setInvites(res.data || []);
        } catch (e) {
            setInvites([]);
        }
    };

    const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

    const handleCreate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            await api.post('/api/admin/invite/create', null, { params: { inviterUserId: userId, ...form } });
            setForm({ firstName: '', lastName: '', email: '' });
            setModalOpen(false);
            fetchInvites(schoolId);
            setSnackbar({ open: true, message: 'Invite sent successfully!', severity: 'success' }); // NEW: show snackbar on success
        } catch (err) {
            let backendMsg = err?.response?.data?.message;
            // Axios may not parse error message for 403, so try to extract from response text
            if (!backendMsg && err?.response?.data) {
                if (typeof err.response.data === 'string') {
                    // Try to extract message from string
                    const match = err.response.data.match(/"message"\s*:\s*"([^"]+)"/);
                    if (match) backendMsg = match[1];
                }
            }
            if (backendMsg && backendMsg.includes('already invited or registered')) {
                setSnackbar({ open: true, message: 'This email is already invited or registered for this school.', severity: 'error' });
            } else {
                setSnackbar({ open: true, message: backendMsg || 'Failed to send invite because the user is already on your team or part of another team.', severity: 'error' });
            }
            // Do not setError, only show Snackbar
        } finally {
            setLoading(false);
        }
    };

    const handleCancelInvite = (id) => {
        setPendingDelete({ id, type: 'invite' });
        setConfirmOpen(true);
    };

    const handleRemoveCollaborator = (userId) => {
        setPendingDelete({ userId, type: 'collab' });
        setConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (pendingDelete) {
            if (pendingDelete.type === 'invite') {
                await api.post('/api/admin/invite/cancel', null, { params: { id: pendingDelete.id } });
                fetchInvites(schoolId);
            } else if (pendingDelete.type === 'collab') {
                try {
                    await api.post('/api/admin/invite/remove-collab-by-userid', null, { params: { userId: pendingDelete.userId } });
                    setSnackbar({ open: true, message: 'Collaborator removed successfully!', severity: 'success' });
                    fetchInvites(schoolId);
                } catch (err) {
                    // Surface backend error when possible (403/permission or server FK errors)
                    const status = err?.response?.status;
                    const backendMsg = err?.response?.data?.message || err?.response?.data;
                    const msg = backendMsg || (status ? `Failed to remove collaborator (${status})` : 'Failed to remove collaborator.');
                    // log for developer debugging
                    // eslint-disable-next-line no-console
                    console.error('remove collaborator error', err);
                    setSnackbar({ open: true, message: msg, severity: 'error' });
                }
            } else if (pendingDelete.type === 'contact') {
                try {
                    await api.delete(`/api/admin/contacts/${pendingDelete.id}`);
                    setSnackbar({ open: true, message: 'Contact deleted', severity: 'success' });
                    fetchContacts(schoolId);
                } catch (err) {
                    const status = err?.response?.status;
                    const backendMsg = err?.response?.data?.message || err?.response?.data;
                    const msg = backendMsg || (status ? `Failed to delete contact (${status})` : 'Failed to delete contact.');
                    // eslint-disable-next-line no-console
                    console.error('delete contact error', err);
                    setSnackbar({ open: true, message: msg, severity: 'error' });
                }
            }
        }
        setConfirmOpen(false);
        setPendingDelete(null);
    };

    const handleCancelDialog = () => {
        setConfirmOpen(false);
        setPendingDelete(null);
    };

    const handleResendInvite = async (id) => {
        try {
            await api.post('/api/admin/invite/resend', null, { params: { id } });
            setSnackbar({ open: true, message: 'Invite resent successfully!', severity: 'success' });
            fetchInvites(schoolId);
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to resend invite.', severity: 'error' });
        }
    };

    const handleEditClick = (inv) => {
        const statusLabel = getStatus(inv.status).label;
        setEditForm({
            id: inv.id,
            firstName: inv.inviteeFirstName || inv.firstName || '',
            lastName: inv.inviteeLastName || inv.lastName || '',
            email: inv.inviteeEmail || inv.email || '',
            status: statusLabel,
        });
        setEditModalOpen(true);
    };

    const handleEditChange = (e) => setEditForm({ ...editForm, [e.target.name]: e.target.value });

    const handleEditSave = async (e) => {
        e.preventDefault();
        if (!editForm.id) return;
        setEditLoading(true);
        try {
            await api.post('/api/admin/invite/update', null, { params: { id: editForm.id, firstName: editForm.firstName, lastName: editForm.lastName, email: editForm.email } });
            setSnackbar({ open: true, message: 'Invite updated successfully!', severity: 'success' });
            setEditModalOpen(false);
            fetchInvites(schoolId);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data || 'Failed to update invite.';
            setSnackbar({ open: true, message: msg, severity: 'error' });
        } finally {
            setEditLoading(false);
        }
    };


    // Helper: get status label and color
    const getStatus = (status) => {
        if (status === 'PENDING' || status === 'Invited') return { label: 'Invited', color: 'text.secondary' };
        if (status === 'ACCEPTED' || status === 'Active') return { label: 'Active', color: 'success.main' };
        return { label: status, color: 'text.secondary' };
    };

    // Contacts state & handlers
    const [contacts, setContacts] = useState([]);
    const [contactModalOpen, setContactModalOpen] = useState(false);
    const [contactForm, setContactForm] = useState({ firstName: '', lastName: '', email: '', title: '' });
    const [contactEditOpen, setContactEditOpen] = useState(false);
    const [contactEditForm, setContactEditForm] = useState({ id: null, firstName: '', lastName: '', email: '', title: '' });
    const [contactLoading, setContactLoading] = useState(false);

    const fetchContacts = async (sid) => {
        try {
            const res = await api.get(`/api/admin/contacts?schoolId=${sid}`);
            setContacts(res.data || []);
        } catch (err) {
            setContacts([]);
        }
    };

    const handleOpenAddContact = () => {
        setContactForm({ firstName: '', lastName: '', email: '', title: '' });
        setContactModalOpen(true);
    };

    const handleContactChange = (e) => setContactForm({ ...contactForm, [e.target.name]: e.target.value });

    const handleCreateContact = async (e) => {
        e.preventDefault();
        setContactLoading(true);
        try {
            await api.post(`/api/admin/contacts`, contactForm, { params: { schoolId } });
            setContactModalOpen(false);
            setSnackbar({ open: true, message: 'Contact added', severity: 'success' });
            fetchContacts(schoolId);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data || 'Failed to create contact.';
            setSnackbar({ open: true, message: msg, severity: 'error' });
        } finally {
            setContactLoading(false);
        }
    };

    const handleEditContactClick = (c) => {
        setContactEditForm({ id: c.id, firstName: c.firstName || '', lastName: c.lastName || '', email: c.email || '', title: c.title || '' });
        setContactEditOpen(true);
    };

    const handleContactEditChange = (e) => setContactEditForm({ ...contactEditForm, [e.target.name]: e.target.value });

    const handleSaveContactEdit = async (e) => {
        e.preventDefault();
        if (!contactEditForm.id) return;
        setContactLoading(true);
        try {
            await api.put(`/api/admin/contacts/${contactEditForm.id}`, contactEditForm);
            setContactEditOpen(false);
            setSnackbar({ open: true, message: 'Contact updated', severity: 'success' });
            fetchContacts(schoolId);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data || 'Failed to update contact.';
            setSnackbar({ open: true, message: msg, severity: 'error' });
        } finally {
            setContactLoading(false);
        }
    };

    const handleDeleteContact = (id) => {
        setPendingDelete({ id, type: 'contact' });
        setConfirmOpen(true);
    };

    // Helper: is account owner (for now, first accepted user or by email match)
    const isOwner = (invite) => {
        // You may want to adjust this logic based on your backend
        return invite.isOwner || invite.isAccountOwner || invite.role === 'ADMIN';
    };

    return (
        <>
            <Box component="main">
                <Grid container spacing={4} alignItems="flex-start" sx={{ mt: 2 }}>
                    <Grid item xs={12} md={4} lg={3}>
                        <LeftPanel items={[
                            <Step key="step1">1. Invite collaborators</Step>,
                            'Add teammates to help with the application.',
                            <Step key="step2">2. Send invites</Step>,
                            'We’ll email them instructions to join.',
                            <Step key="step3">3. Work together</Step>,
                            'Work together to complete the application.',
                            <Step key="step4">4. Add contacts</Step>,
                            'Add contacts that you would like to be notified of your application progress as well as receive valuable Retool Your School information throughout the program year.',
                        ]} />
                    </Grid>
                    <Grid item xs={12} md={9} lg={9}>
                        <Paper elevation={1} sx={{ p: { xs: 2, md: 4 }, minWidth: 700, mx: 'auto', maxWidth: { xs: '100%', md: '950px' } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>Collaborators</Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setModalOpen(true)}
                                    sx={{ boxShadow: 2, textTransform: 'none', fontWeight: 600 }}
                                    disabled={userRole === 'COLLAB'}
                                >
                                    INVITE TEAM MEMBERS
                                </Button>
                            </Box>
                            {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Username/Email</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>First Name</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Last Name</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {invites.map((inv) => {
                                            const status = getStatus(inv.status);
                                            return (
                                                <TableRow key={inv.id}>
                                                    <TableCell>
                                                        {isOwner(inv) && <StarIcon fontSize="small" color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />}
                                                        {inv.inviteeEmail || inv.email}
                                                    </TableCell>
                                                    <TableCell>{inv.inviteeFirstName || inv.firstName}</TableCell>
                                                    <TableCell>{inv.inviteeLastName || inv.lastName}</TableCell>
                                                    <TableCell>
                                                        <Typography sx={{ color: status.color, fontWeight: 500 }}>{status.label}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {status.label === 'Invited' && (
                                                            <>
                                                                <IconButton aria-label="edit" size="small" disabled={userRole !== 'ADMIN'} onClick={() => handleEditClick(inv)} title={userRole === 'ADMIN' ? 'Edit invite' : 'Only admins can edit'} sx={{ color: '#1976d2' }}>
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    aria-label="delete"
                                                                    size="small"
                                                                    onClick={() => handleCancelInvite(inv.id)}
                                                                    disabled={isOwner(inv) || userRole === 'COLLAB'}
                                                                    title={isOwner(inv) ? 'Account owner cannot be removed' : 'Cancel Invite'}
                                                                    sx={{ color: '#d32f2f' }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                                <Button variant="outlined" size="small" sx={{ ml: 1, borderColor: 'primary.main', color: 'primary.main', fontWeight: 600 }} onClick={() => handleResendInvite(inv.id)} disabled={isOwner(inv) || userRole === 'COLLAB'}>
                                                                    RESEND INVITE
                                                                </Button>
                                                            </>
                                                        )}
                                                        {status.label === 'Active' && (
                                                            <>
                                                                <IconButton aria-label="edit" size="small" disabled={userRole !== 'ADMIN'} onClick={() => handleEditClick(inv)} title={userRole === 'ADMIN' ? 'Edit collaborator' : 'Only admins can edit'} sx={{ color: '#1976d2' }}>
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    aria-label="delete"
                                                                    size="small"
                                                                    onClick={() => handleRemoveCollaborator(inv.id)}
                                                                    disabled={isOwner(inv) || userRole === 'COLLAB'}
                                                                    title={isOwner(inv) ? 'Account owner cannot be removed' : 'Remove Collaborator'}
                                                                    sx={{ color: '#d32f2f' }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                                {/* Confirm Delete Dialog */}
                                                                <Modal open={confirmOpen} onClose={handleCancelDialog}>
                                                                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 4, minWidth: 350 }}>
                                                                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Are you sure?</Typography>
                                                                        <Typography sx={{ mb: 3 }}>This action is <b>permanent</b> and cannot be undone.</Typography>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                                                            <Button onClick={handleCancelDialog}>Cancel</Button>
                                                                            <Button variant="contained" color="error" onClick={handleConfirmDelete}>Delete</Button>
                                                                        </Box>
                                                                    </Box>
                                                                </Modal>
                                                            </>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                                <StarIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                                <Typography variant="body2" color="text.secondary">Indicates Account Administrator</Typography>
                            </Box>
                        </Paper>

                        {/* Contacts section */}
                        <Paper elevation={1} sx={{ p: { xs: 2, md: 4 }, minWidth: 700, mx: 'auto', maxWidth: { xs: '100%', md: '950px' }, mt: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>Contacts</Typography>
                                <Button variant="contained" color="primary" onClick={handleOpenAddContact} disabled={userRole === 'COLLAB'} sx={{ boxShadow: 2, textTransform: 'none', fontWeight: 600 }}>ADD CONTACTS</Button>
                            </Box>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>First Name</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Last Name</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {contacts.map((c) => (
                                            <TableRow key={c.id}>
                                                <TableCell>{c.email}</TableCell>
                                                <TableCell>{c.firstName}</TableCell>
                                                <TableCell>{c.lastName}</TableCell>
                                                <TableCell>{c.title}</TableCell>
                                                <TableCell>
                                                    <IconButton aria-label="edit" size="small" disabled={userRole !== 'ADMIN'} onClick={() => handleEditContactClick(c)} sx={{ color: '#1976d2' }}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton aria-label="delete" size="small" onClick={() => handleDeleteContact(c.id)} disabled={userRole === 'COLLAB'} sx={{ color: '#d32f2f' }}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Add Contact Modal */}
                            <Modal open={contactModalOpen} onClose={() => setContactModalOpen(false)}>
                                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 4, minWidth: 400 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Add Contact</Typography>
                                    <Box component="form" onSubmit={handleCreateContact} noValidate>
                                        <TextField label="First Name" name="firstName" value={contactForm.firstName} onChange={handleContactChange} fullWidth required sx={{ mb: 2 }} />
                                        <TextField label="Last Name" name="lastName" value={contactForm.lastName} onChange={handleContactChange} fullWidth required sx={{ mb: 2 }} />
                                        <TextField label="Email" name="email" type="email" value={contactForm.email} onChange={handleContactChange} fullWidth required sx={{ mb: 2 }} />
                                        <TextField label="Title" name="title" value={contactForm.title} onChange={handleContactChange} fullWidth sx={{ mb: 2 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                            <Button onClick={() => setContactModalOpen(false)} sx={{ mr: 2 }}>Cancel</Button>
                                            <Button type="submit" variant="contained" color="primary" disabled={contactLoading}>Add</Button>
                                        </Box>
                                    </Box>
                                </Box>
                            </Modal>

                            {/* Edit Contact Modal */}
                            <Modal open={contactEditOpen} onClose={() => setContactEditOpen(false)}>
                                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 4, minWidth: 400 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Edit Contact</Typography>
                                    <Box component="form" onSubmit={handleSaveContactEdit} noValidate>
                                        <TextField label="First Name" name="firstName" value={contactEditForm.firstName} onChange={handleContactEditChange} fullWidth required sx={{ mb: 2 }} />
                                        <TextField label="Last Name" name="lastName" value={contactEditForm.lastName} onChange={handleContactEditChange} fullWidth required sx={{ mb: 2 }} />
                                        <TextField label="Email" name="email" type="email" value={contactEditForm.email} onChange={handleContactEditChange} fullWidth required sx={{ mb: 2 }} />
                                        <TextField label="Title" name="title" value={contactEditForm.title} onChange={handleContactEditChange} fullWidth sx={{ mb: 2 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                            <Button onClick={() => setContactEditOpen(false)} sx={{ mr: 2 }}>Cancel</Button>
                                            <Button type="submit" variant="contained" color="primary" disabled={contactLoading}>Update</Button>
                                        </Box>
                                    </Box>
                                </Box>
                            </Modal>
                        </Paper>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Button
                                variant="outlined"
                                color="inherit"
                                onClick={async () => {
                                    try { localStorage.removeItem('showInviteOnFirstLogin'); } catch (e) { }
                                    setShowInviteOnFirstLogin(false);
                                    if (schoolProfileExists && schoolProfileCompleted) navigate('/grantlanding');
                                    else navigate('/schoolinfo');
                                }}
                                sx={{ textTransform: 'none', fontWeight: 600, px: 4 }}
                            >
                                {showInviteOnFirstLogin ? 'Skip & Continue' : 'Return To Grants'}
                            </Button>
                        </Box>
                        {showInviteOnFirstLogin && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 700, textAlign: 'center' }}>
                                    If you'd like to invite collaborators at a later time, please use the hamburger menu at the top right of the screen and choose "Invite Collaborators".
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                </Grid>

                {/* Invite Modal */}
                <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 4, minWidth: 400 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Invite Team Member</Typography>
                        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
                        <Box component="form" onSubmit={handleCreate} noValidate>
                            <TextField label="First Name" name="firstName" value={form.firstName} onChange={handleChange} fullWidth required sx={{ mb: 2 }} />
                            <TextField label="Last Name" name="lastName" value={form.lastName} onChange={handleChange} fullWidth required sx={{ mb: 2 }} />
                            <TextField label="Email" name="email" type="email" value={form.email} onChange={handleChange} fullWidth required sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                <Button onClick={() => setModalOpen(false)} sx={{ mr: 2 }}>Cancel</Button>
                                <Button type="submit" variant="contained" color="primary" disabled={loading || !schoolId || invites.filter(i => i.status === 'PENDING' || i.status === 'ACCEPTED').length >= 3}>
                                    Invite
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Modal>
                {/* Edit Invite Modal */}
                <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 4, minWidth: 400 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Edit Invite / Collaborator</Typography>
                        <Box component="form" onSubmit={handleEditSave} noValidate>
                            <TextField label="First Name" name="firstName" value={editForm.firstName} onChange={handleEditChange} fullWidth required sx={{ mb: 2 }} />
                            <TextField label="Last Name" name="lastName" value={editForm.lastName} onChange={handleEditChange} fullWidth required sx={{ mb: 2 }} />
                            <TextField label="Email" name="email" type="email" value={editForm.email} onChange={handleEditChange} fullWidth required sx={{ mb: 2 }} disabled={editForm.status === 'Active'} helperText={editForm.status === 'Active' ? 'Email cannot be changed after invite is accepted' : ''} />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                <Button onClick={() => setEditModalOpen(false)} sx={{ mr: 2 }}>Cancel</Button>
                                <Button type="submit" variant="contained" color="primary" disabled={editLoading}>
                                    Save
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Modal>
            </Box>
            {/* Snackbar notification */}
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}
export default InviteCollab;
